import React, { useMemo } from 'react';

import { useTranslation } from '@/i18n';
import { colors } from '@/assets/styles/colors';
import { ConfirmModal } from '@/components/ui/modals/ConfirmModal';
import { TextArea } from '@/components/ui/inputs';

import type {
  CompanyShiftRequest,
  LC_BulkRejectShiftRequestsOut,
} from '@/api/domains/shift/shift.types';
import { SHIFT_COMMENT_MAX_LENGTH } from '@/api/domains/shift/shift.types';
import { useShiftChangeRequestBulkRejection } from '@/hooks/attendance/useShiftChangeRequestBulkRejection';


interface ShiftChangeRequestBulkRejectionModalProps {
  isOpen: boolean;
  selectedRequests: CompanyShiftRequest[];
  onClose: () => void;
  onSuccess: (result: LC_BulkRejectShiftRequestsOut) => void;
}

export const ShiftChangeRequestBulkRejectionModal: React.FC<
  ShiftChangeRequestBulkRejectionModalProps
> = ({
  isOpen,
  selectedRequests,
  onClose,
  onSuccess,
}) => {
  const { t: translate } = useTranslation();

  const selectedRequestIds = useMemo(
    () => selectedRequests.map((request) => request.id),
    [selectedRequests],
  );

  const { comment, setComment, canSubmit, isSubmitting, handleSubmit } =
    useShiftChangeRequestBulkRejection({
      selectedRequestIds,
      onSuccess,
    });

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="shift-bulk-rejection-modal-backdrop"
    >
      <ConfirmModal
        variant="error"
        title={translate('attendance:shiftChangeAdmin.bulk.rejectTitle')}
        bodyText={translate('attendance:shiftChangeAdmin.bulk.selectedCount', {
          count: selectedRequests.length,
        })}
        onConfirm={handleSubmit}
        onCancel={onClose}
        onClose={onClose}
        confirmText={translate('attendance:shiftChangeAdmin.bulk.reject')}
        cancelText={translate('attendance:shiftChangeAdmin.bulk.cancel')}
        isLoading={isSubmitting}
        isConfirmDisabled={!canSubmit}
        cardClassName="w-full max-w-[700px] max-h-[90vh] overflow-y-auto"
      >
        {/* Lista de solicitudes seleccionadas */}
        <div
          className="border rounded-2xl overflow-hidden"
          style={{ borderColor: colors.primary.auxiliar }}
        >
          <div className="overflow-y-auto max-h-[280px]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" style={{ borderColor: colors.primary.auxiliar }}>
                  <th className="text-left px-4 py-2 font-medium text-black">
                    {translate('attendance:shiftChangeAdmin.table.id')}
                  </th>
                  <th className="text-left px-4 py-2 font-medium text-black">
                    {translate('attendance:shiftChangeAdmin.table.employee')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {selectedRequests.map((request, index) => (
                  <tr
                    key={request.id}
                    style={{
                      backgroundColor:
                        index % 2 === 0 ? colors.grays.neutralFA : colors.grays.neutralFF,
                    }}
                  >
                    <td className="px-4 py-2" style={{ color: colors.grays.neutral66 }}>
                      {request.id}
                    </td>
                    <td className="px-4 py-2" style={{ color: colors.grays.neutral66 }}>
                      {request.employeeName}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Campo de comentario obligatorio */}
        <TextArea
          label={translate('attendance:shiftChangeAdmin.bulk.commentLabel')}
          value={comment}
          onChange={setComment}
          required
          maxLength={SHIFT_COMMENT_MAX_LENGTH}
          showCharacterCount
          data-testid="shift-bulk-rejection-comment-input"
        />
      </ConfirmModal>
    </div>
  );
};

export default ShiftChangeRequestBulkRejectionModal;
